import type { CategoriaPremio } from 'types/pessoa';

/**
 * Constantes do Regulamento Prêmio BB 2027 usadas na pré-inscrição.
 */

/**
 * Direcionador transversal da ECBB 2026-2030 (regulamento 7.1.5.1), como vem do
 * aggregate `direcionadores_transversais` (coleção `direcionadores_transversais__c`).
 */
export interface DirecionadorTransversal {
  _id: string;
  nome: string;
  descricao: string;
}

/** Pergunta obrigatória da pré-inscrição (7.1.7.5.1.3), com o nome do direcionador escolhido. */
export const perguntaNorteadora = (nomeDirecionador?: string) =>
  `Por que esta iniciativa foi fundamental para o cumprimento do direcionador transversal${
    nomeDirecionador ? ` "${nomeDirecionador}"` : ''
  } ao qual está vinculada e quais resultados foram obtidos?`;

/**
 * Textos dos "?" do formulário: conteúdo obrigatório e orientação de cada campo,
 * copiados do quadro do item 7.1.7.2 do regulamento.
 */
export const AJUDA_DOS_CAMPOS = {
  nome: 'Título objetivo e representativo.\n\nDeve identificar a essência da ação e, preferencialmente, refletir o impacto ou a solução gerada.',
  direcionador:
    'Selecione o Direcionador Transversal que melhor represente o objetivo e o resultado da entrega.',
  unidade:
    'Superintendência Estadual ou Diretoria gestora da rede do prefixo em que o funcionário foi avaliado.\n\nA unidade aprovará o bloco, o indicador, a superação da meta e o alinhamento ao Propósito do BB.',
  semestre: 'Primeiro ou segundo semestre de 2026.\n\nDeve refletir o período efetivo de execução e geração dos resultados.',
  participantes: 'Inclua os Integrantes que efetivamente participaram da iniciativa.',
  descricao:
    'Problema, desafio ou oportunidade; solução; principais ações; e diferencial.\n\nO texto deve permitir a compreensão objetiva da atuação do time e da relevância da iniciativa.',
  pergunta:
    'Resultados concretos e conexão com o indicador.\n\nSempre que possível, apresentar dados objetivos e mensuráveis, como percentual de superação, evolução, impactos ou ganhos.',
  // Fora do quadro 7.1.7.2: item 7.1.7.5.1.2, que vale para as categorias ECBB.
  categoriaEcbb:
    'A iniciativa deverá ser vinculada a uma categoria ECBB e a um Direcionador Transversal, de acordo com seu objetivo e com os resultados obtidos.',
};

export interface Semestre2026 {
  valor: '1' | '2';
  rotulo: string;
  start: string;
  end: string;
}

/** Semestres aceitos na pré-inscrição 2027 (7.1.7.2), com início e fim canônicos (horário de Brasília). */
export const SEMESTRES_2026: Semestre2026[] = [
  { valor: '1', rotulo: '1º Semestre de 2026', start: '2026-01-01T03:00:00.000Z', end: '2026-07-01T02:59:59.999Z' },
  { valor: '2', rotulo: '2º Semestre de 2026', start: '2026-07-01T03:00:00.000Z', end: '2027-01-01T02:59:59.999Z' },
];

export const getRotuloSemestre = (semestre?: number) =>
  SEMESTRES_2026.find((s) => Number(s.valor) === semestre)?.rotulo;

export type CategoriaEcbb = Extract<
  CategoriaPremio,
  'ECBB_PERENIDADE' | 'ECBB_CLIENTES' | 'ECBB_CULTURA_PESSOAS' | 'ECBB_TECNOLOGIA' | 'ECBB_NOVAS_ARENAS'
>;

/** Categorias ECBB 2026-2030 (7.1.4.1.1 a 7.1.4.1.5). */
export const CATEGORIAS_ECBB: { id: CategoriaEcbb; nome: string }[] = [
  { id: 'ECBB_PERENIDADE', nome: 'Alicerces para a Perenidade' },
  { id: 'ECBB_CLIENTES', nome: 'Clientes' },
  { id: 'ECBB_CULTURA_PESSOAS', nome: 'Pessoas e Cultura' },
  { id: 'ECBB_TECNOLOGIA', nome: 'Tecnologias como Viabilizadoras de Negócios' },
  { id: 'ECBB_NOVAS_ARENAS', nome: 'Novas Arenas de Negócios' },
];

export const getNomeCategoriaEcbb = (id?: string) => CATEGORIAS_ECBB.find((c) => c.id === id)?.nome;

/** Categorias de Negócios e Atendimento, escolhidas antes do formulário. */
export const CATEGORIAS_CONEXAO = [
  { id: 'NEGOCIOS_RESULTADOS', nome: 'Negócios e Resultados' },
  { id: 'ATENDIMENTO_EXPERIENCIA', nome: 'Atendimento e Experiência do Cliente' },
];

export const getNomeCategoriaConexao = (id?: string) => CATEGORIAS_CONEXAO.find((c) => c.id === id)?.nome;
